/**
 * Canonical application role definitions.
 *
 * The `code` values MUST exactly match the Keycloak realm role names.
 * This is the single source of truth for roles in the frontend.
 *
 * `description` is a display-only label — it is NEVER used for comparisons,
 * routing decisions, or API calls.
 */
export const ROLES = {
    SYSTEM_ADMIN:     { code: 'SYSTEM_ADMIN',     description: 'System Administrator' },
    TENANT_OWNER:     { code: 'TENANT_OWNER',     description: 'Supplier / Owner' },
    TENANT_ADMIN:     { code: 'TENANT_ADMIN',     description: 'Supplier Admin' },
    SALES_MANAGER:    { code: 'SALES_MANAGER',    description: 'Sales Manager' },
    SALES_PERSON:     { code: 'SALES_PERSON',     description: 'Sales Person' },
    WAREHOUSE_PERSON: { code: 'WAREHOUSE_PERSON', description: 'Warehouse Staff' },
    USER:             { code: 'USER',             description: 'Standard User' },
};

/**
 * All known role codes for quick lookup.
 * Used to filter out Keycloak system roles (offline_access, uma_authorization, etc.).
 */
export const KNOWN_ROLE_CODES = new Set(Object.keys(ROLES));

/**
 * Get the human-readable description for a role code.
 * Falls back to formatting the raw code (e.g. SALES_PERSON → "SALES PERSON").
 * @param {string} code - Keycloak realm role name (e.g. 'SALES_PERSON')
 * @returns {string} Display description
 */
export const getRoleDescription = (code) => {
    if (!code) return '';
    return ROLES[code]?.description ?? code.replace(/_/g, ' ');
};

/**
 * Normalize a raw role string from a JWT token to its canonical AppRole code.
 * Strips 'ROLE_' prefix and handles known legacy aliases.
 * @param {string} rawRole
 * @returns {string|null} Canonical role code, or null if not a known app role
 */
export const normalizeRole = (rawRole) => {
    if (!rawRole) return null;
    // Strip Spring Security prefix
    let code = rawRole.toUpperCase().replace(/^ROLE_/, '');
    // Legacy aliases
    if (code === 'SALESMAN')  code = 'SALES_PERSON';
    if (code === 'WAREHOUSE') code = 'WAREHOUSE_PERSON';
    if (code === 'ADMIN')     code = 'SYSTEM_ADMIN';
    if (code === 'SUPPLIER')  code = 'TENANT_OWNER';
    // Return only if it's a known role
    return KNOWN_ROLE_CODES.has(code) ? code : null;
};
